// Implements: F-IDN-012, F-IDN-014, F-UIX-009
/**
 * The Perfil card of the v3 mockup (`acctProfile`): the photo, the editable full name, the
 * read-only e-mail, the 2FA row with its "Gestionar" link, "Dónde participas" (owner 2026-10-19) and
 * "En Valtx desde …" beside the save button. Pure presentation with the draft name in local state; saving goes up through `onSave`,
 * which answers whether the server kept it, and a server refusal comes back as `serverError`.
 *
 * "Guardar cambios" is disabled until the trimmed name differs from the saved one and is not
 * blank, so a click never sends a no-op. A refusal focuses the field it is about.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { ReactElement, ReactNode, SyntheticEvent } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import type { MeSummary } from "../../../types/api";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Button, buttonClassName } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field, describedBy } from "../../../shared/ui/molecules/Field";
import { dateText } from "../model/dates";
import shared from "./account.module.css";

/** The Contract's bound on a trimmed name (`FullName`, 1..120). */
export const NAME_MAX = 120;

export interface ProfileFormProps {
  readonly me: MeSummary;
  /** The photo editor, composed by the container. */
  readonly avatar: ReactNode;
  readonly saving: boolean;
  /** The server's message for a refused name, or `null`. */
  readonly serverError: string | null;
  /** Save the trimmed name; answers `true` when the server kept it. */
  readonly onSave: (fullName: string) => Promise<boolean>;
  /** Where "Gestionar" leads (the 2FA section). */
  readonly totpHref: string;
  /** "Dónde participas" (owner 2026-10-19), composed by the container; nothing when absent. */
  readonly participation?: ReactNode;
}

export function ProfileForm({
  me,
  avatar,
  saving,
  serverError,
  onSave,
  totpHref,
  participation,
}: ProfileFormProps): ReactElement {
  const { t } = useTranslation();
  const titleId = useId();
  const nameId = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(me.full_name);
  const [localError, setLocalError] = useState<string | null>(null);

  const trimmed = name.trim();
  const dirty = trimmed !== "" && trimmed !== me.full_name;
  const error = localError ?? serverError ?? undefined;

  useEffect(() => {
    if (serverError !== null) nameRef.current?.focus();
  }, [serverError]);

  const submit = async (event?: SyntheticEvent): Promise<void> => {
    event?.preventDefault();
    if (trimmed === "") {
      setLocalError(t("account.profile.nameRequired"));
      nameRef.current?.focus();
      return;
    }
    if (trimmed.length > NAME_MAX) {
      setLocalError(t("account.profile.nameTooLong"));
      nameRef.current?.focus();
      return;
    }
    if (!dirty) return;
    if (await onSave(trimmed)) setName(trimmed);
  };

  return (
    <form
      className={shared.card}
      aria-labelledby={titleId}
      noValidate
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <div className={shared.head}>
        <h2 className={shared.title} id={titleId}>
          {t("account.profile.title")}
        </h2>
      </div>
      <div className={shared.body}>
        {avatar}
        <Field id={nameId} label={t("account.profile.name")} hint={t("account.profile.nameHelp")} error={error}>
          <Input
            id={nameId}
            inputRef={nameRef}
            value={name}
            autoComplete="name"
            invalid={error !== undefined}
            describedBy={describedBy(nameId, { hint: t("account.profile.nameHelp"), error })}
            onChange={(value) => {
              setName(value);
              setLocalError(null);
            }}
          />
        </Field>
        <div className={shared.field}>
          <span className={shared.label}>{t("account.profile.email")}</span>
          <div className={shared.readOnly}>{me.email}</div>
          <p className={shared.help}>{t("account.profile.emailHelp")}</p>
        </div>
        <div className={shared.field}>
          <span className={shared.label}>{t("account.profile.security")}</span>
          <div className={shared.securityRow}>
            <Icon name="lock" size="sm" />
            <span className={shared.grow}>{t("account.profile.totp")}</span>
            {me.totp_enabled ? (
              <Badge tone="success">
                {t("account.profile.totpOnSince", { date: dateText(me.totp_enabled_since, "short") })}
              </Badge>
            ) : (
              <Badge tone="neutral">{t("account.profile.totpOff")}</Badge>
            )}
            <Link className={buttonClassName("ghost", "sm")} to={totpHref}>
              {t("account.profile.manage")}
            </Link>
          </div>
        </div>
        {participation}
      </div>
      <div className={shared.foot}>
        <span className={shared.footMeta}>
          {t("account.profile.memberSince", { date: dateText(me.member_since, "long") })}
        </span>
        <Button variant="primary" type="submit" disabled={!dirty || saving} loading={saving}>
          {t("account.profile.save")}
        </Button>
      </div>
    </form>
  );
}
